import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { routes } from '../../routes/AppRoutes';
import { SeparationType } from '../../enums/SeparationType';
import { LinkedToOrderBatchOption } from '../../enums/LinkedToOrderBatchOption';
import { OrderAlertType } from '../../enums/OrderAlertType';
import { OrderShippingLineState } from '../../enums/OrderShippingLineState';
import { FramesQuantity } from '../../enums/FramesQuantity';
import { GlobalPickerPerformType } from '../../enums/GlobalPickerPerformType';
import { ProfileType } from '../../enums/ProfileType';
import { FileFormat } from '../../enums/FileFormat';
import { TSOBarcodeMode } from '../../enums/TSOBarcodeMode';
import { DanfeLabelBarcodeMode } from '../../enums/DanfeLabelBarcodeMode';
import { SessionHelper } from '../../helpers/SessionHelper';
import ServicesContext from '../../contexts/ServicesContext';
import { omnichannelResources } from '../../resources/omnichannelResources';
import { useBaseListOrders } from '../../hooks/useBaseListOrders';
import { MainActions } from '../../actions/MainActions';
import { FrontVersionResources } from '../../models/FrontVersion';
import { OrderForListing } from '../../models/OrderForListing';
import { FilterOrderInput } from '../../services/Orders/FilterOrder/FilterOrderInput';
import { ActionsForOrdersSelection } from '../ActionsForOrdersSelection/ActionsForOrdersSelection';
import { ActionsPanel } from '../ActionsPanel/ActionsPanel';
import { PaginatedOrderList } from '../PaginatedOrderList/PaginatedOrderList';
import { ModalUpdateStateForSelectedOrders } from '../ModalUpdateStateForSelectedOrders/ModalUpdateStateForSelectedOrders';
import { IFilterToListOrdersOutput, ModalFiltersToListOrders, getAppliedStatePrescriptionIds } from '../ModalFiltersToListOrders/ModalFiltersToListOrders';
import { AppliedFiltersToListOrders } from '../AppliedFiltersToListOrders/AppliedFiltersToListOrders';
import { ModalUpdatePrescriptionStateForSelectedOrders } from '../ModalUpdatePrescriptionStateForSelectedOrders/ModalUpdatePrescriptionStateForSelectedOrders';
import { ModalNotifyCustomersToCollectRecipeAndDNPsV2 } from '../ModalNotifyCustomersToCollectRecipeAndDNPsV2/ModalNotifyCustomersToCollectRecipeAndDNPsV2';
import { ModalUpdateStateForSelectedOrdersByGlobalPicker } from '../ModalUpdateStateForSelectedOrdersByGlobalPicker/ModalUpdateStateForSelectedOrdersByGlobalPicker';
import { DanfeLabelPreGenerationModal } from '../DanfeLabelPreGenerationModal/DanfeLabelPreGenerationModal';
import { LongRunningReportProgressModal, ReportType } from '../LongRunningReportProgressModal/LongRunningReportProgressModal';
import { Container } from './styles';

interface IListOrdersProps {
  separationType: SeparationType;
  expressOrder?: boolean;
  listingOrders?: () => void;
}

const ListOrders: React.FC<IListOrdersProps> = ({
  separationType,
  expressOrder,
  listingOrders
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [lastFilter, setLastFilter] = React.useState<FilterOrderInput | null>(null);
  const [lastFilterValues, setLastFilterValues] = React.useState<IFilterToListOrdersOutput | undefined>(undefined);
  const [modalFilterVisible, setModalFilterVisible] = React.useState<boolean>(false);
  const [danfeLabelPreGenerationModalData, setDanfeLabelPreGenerationModalData] = React.useState<{
    visible: boolean;
    ordersForListing: OrderForListing[];
    fileFormat?: FileFormat | null;
  }>({
    visible: false,
    ordersForListing: [],
  });
  const [longRunningReportProgressModalVisible, setLongRunningReportProgressModalVisible] = React.useState<boolean>(false);
  const [longRunningReportProgressModalData, setLongRunningReportProgressModalData] = React.useState<{
    reportType?: ReportType;
    reportVersion?: number;
    orderForListing?: OrderForListing[];
    queueJob?: boolean;
    separationType?: SeparationType | null;
    fileFormat?: FileFormat | null;
    barcodeMode?: TSOBarcodeMode;
    danfeLabelBarcodeMode?: DanfeLabelBarcodeMode;
  }>({});

  const navigate = useNavigate();

  const {
    session, userLocations, signOut,
    ordersForListing,
    currentOrderBy, setCurrentOrderBy,
    currentPage, setCurrentPage,
    currentPageSize,
    amountOfPages,
    totalAmount,
    pageSize, setPageSize,
    modalUpdateStateOrderVisible, setModalUpdateStateOrderVisible,
    selectedOrdersForModalUpdateStateOrder,
    modalUpdateStatePrescriptionVisible, setModalUpdateStatePrescriptionVisible,
    selectedOrdersForModalUpdateStatePrescription,
    selectAll,
    selectOrder,
    handleFilterOrder,
    updateStateOrder,
    updateStatePrescription,
    closeModalUpdateStateOrders,
    closeModalUpdateStatePrescriptions,
    listHasAnySelectedOrder,
    filtering,
    notifyCustomersToCollectRecipeAndDNPs,
    selectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs,
    modalNotifyCustomersToCollectRecipeAndDNPsVisible, setModalNotifyCustomersToCollectRecipeAndDNPsVisible,
    closeModalModalNotifyCustomersToCollectRecipeAndDNPs,
    handleRefreshOrder,
    updateStateOrderByGlobalPicker,
    modalUpdateStateOrderByGlobalPickerVisible, setModalUpdateStateOrderByGlobalPickerVisible,
    selectedOrdersForModalUpdateStateOrderByGlobalPicker,
    globalPickerPerformTypeForModal,
    closeModalUpdateStateOrdersByGlobalPicker
  } = useBaseListOrders();

  const {
    statesService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    //TO DO: cleanup
    clearFilter(pageSize, currentPage);
  }, []);

  const memoModalUpdateStateForSelectedOrdersRender = React.useMemo(() => {
    if (!modalUpdateStateOrderVisible) return null;

    return (
      <ModalUpdateStateForSelectedOrders
        userId={session?.user?.id}
        selectedOrders={selectedOrdersForModalUpdateStateOrder}
        close={() => closeModalUpdateStateOrders()}
        finish={() => finishModalUpdateStateOrders()}
      />
    );
  }, [selectedOrdersForModalUpdateStateOrder, modalUpdateStateOrderVisible, session]);

  const memoModalUpdatePrescriptionStateForSelectedOrdersRender = React.useMemo(() => {
    if (!modalUpdateStatePrescriptionVisible) return null;

    return (
      <ModalUpdatePrescriptionStateForSelectedOrders
        userId={session?.user?.id}
        selectedOrders={selectedOrdersForModalUpdateStatePrescription}
        close={() => closeModalUpdateStatePrescriptions()}
        finish={() => finishModalUpdateStatePrescriptions()}
      />
    );
  }, [selectedOrdersForModalUpdateStatePrescription, modalUpdateStatePrescriptionVisible, session]);

  const memoModalNotifyCustomersToCollectRecipeAndDNPsRender = React.useMemo(() => {
    if (!modalNotifyCustomersToCollectRecipeAndDNPsVisible) return null;

    return (
      <ModalNotifyCustomersToCollectRecipeAndDNPsV2
        userName={session?.user?.name}
        selectedOrders={selectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs}
        close={() => closeModalModalNotifyCustomersToCollectRecipeAndDNPs()}
        finish={() => finishModalNotifyCustomersToCollectRecipeAndDNPs()}
      />
    );
  }, [selectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs, modalNotifyCustomersToCollectRecipeAndDNPsVisible, session]);

  const memoActionsForOrderSelection = React.useMemo(() => {
    const numberAppliedFilter =
      (lastFilterValues?.numbers ? 1 : 0) +
      (lastFilterValues?.startDate ? 1 : 0) +
      (lastFilterValues?.endDate ? 1 : 0) +
      (lastFilterValues?.stateIds && lastFilterValues?.stateIds ? 1 : 0) +
      (getAppliedStatePrescriptionIds(lastFilterValues).length > 0 ? 1 : 0) +
      (lastFilterValues?.customerName ? 1 : 0) +
      (lastFilterValues?.customerEmail ? 1 : 0) +
      (lastFilterValues?.specialTag ? 1 : 0) +
      (lastFilterValues?.orderAlertType && lastFilterValues?.orderAlertType > 0 ? 1 : 0) +
      (lastFilterValues?.locationId && lastFilterValues?.locationId > 0 ? 1 : 0) +
      (lastFilterValues?.sellerId && lastFilterValues?.sellerId > 0 ? 1 : 0) +
      (lastFilterValues?.stateIdsForUserProfile && lastFilterValues?.stateIdsForUserProfile ? 1 : 0) +
      (lastFilterValues?.deliveryDeadlineStartDate ? 1 : 0) +
      (lastFilterValues?.deliveryDeadlineEndDate ? 1 : 0) +
      (lastFilterValues?.deliveryStartDate ? 1 : 0) +
      (lastFilterValues?.deliveryEndDate ? 1 : 0) +
      (lastFilterValues?.lensMaterialName ? 1 : 0) +
      (lastFilterValues?.lensClassName ? 1 : 0) +
      (lastFilterValues?.fulfilledBy ? 1 : 0) +
      (lastFilterValues?.originedBy ? 1 : 0) +
      (lastFilterValues?.ignoreDefaultUserProfileFilters ? 1 : 0) +
      (lastFilterValues?.salesTypeFilter ? 1 : 0) +
      (lastFilterValues?.framesQuantity && lastFilterValues?.framesQuantity > 0 ? 1 : 0);

    return (
      <ActionsForOrdersSelection
        listHasAnySelectedOrder={listHasAnySelectedOrder}
        separationType={separationType}
        ordersOfBatch={false}
        filtering={filtering}
        appliedFilters={numberAppliedFilter}
        userProfile={session?.user?.profileType}
        updateList={() => updateList(pageSize, currentPage, currentOrderBy)}
        filterList={() => setModalFilterVisible(true)}
        clearFilter={() => clearFilter(pageSize, 1)}
        updateStateOrder={updateStateOrderInBatch}
        updateStatePrescription={updateStatePrescriptionInBatch}
        printListToSeparate={() => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.SeparationList,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printLensSeparationList={(fileFormat) => {
          setLongRunningReportProgressModalData({
            reportType: fileFormat == FileFormat.Pdf ? ReportType.LensesSeparationListPdf : ReportType.LensesSeparationListCsv,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: fileFormat,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printShippingGuide={(version) => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.ShippingGuide,
            reportVersion: version,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        queuePrintShippingGuide={(version) => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.ShippingGuide,
            reportVersion: version,
            orderForListing: ordersForListing,
            queueJob: true,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printTSO={(version, barcodeMode) => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.TSO,
            reportVersion: version,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
            barcodeMode: barcodeMode,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printZplDanfePackingLabel={(barcodeMode) => printDanfePackingLabelByFormat(ordersForListing, FileFormat.Zpl, barcodeMode)}
        printZipZplDanfePackingLabel={(barcodeMode) => printDanfePackingLabelByFormat(ordersForListing, FileFormat.ZipZpl, barcodeMode)}
        printPdfDanfePackingLabel={() => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.DanfePackingLabelPdf,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printZplSimplePackingLabel={() => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.SimplePackingLabelZpl,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printPdfSimplePackingLabel={() => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.SimplePackingLabelPdf,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printZplLensCertificateLabel={() => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.LensCertificateLabelZpl,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        printPdfLensCertificateLabel={() => {
          setLongRunningReportProgressModalData({
            reportType: ReportType.LensCertificateLabelPdf,
            reportVersion: 0,
            orderForListing: ordersForListing,
            queueJob: false,
            separationType: separationType,
            fileFormat: null,
          });
          setLongRunningReportProgressModalVisible(true);
        }}
        notifyCustomersToCollectRecipeAndDNPs={notifyCustomersToCollectRecipeAndDNPs}
        updateStateOrderByGlobalPicker={updateStateOrderByGlobalPickerInBatch}
      />
    );

  }, [separationType, filtering, ordersForListing, lastFilterValues, expressOrder]);

  var memoAppliedFiltersToListOrders = React.useMemo(() => {
    return <AppliedFiltersToListOrders appliedFilters={lastFilterValues} />
  }, [lastFilter, lastFilterValues, filtering, ordersForListing, currentPage, currentPageSize, pageSize]);

  var memoModalFilter = React.useMemo(() => {
    if (!modalFilterVisible) return null;

    return (
      <ModalFiltersToListOrders
        appliedFilters={lastFilterValues}
        separationType={separationType}
        close={() => setModalFilterVisible(false)}
        finish={(values) => changeFilter(values)}
      />
    );
  }, [modalFilterVisible, separationType, lastFilter, lastFilterValues, filtering, ordersForListing, expressOrder]);

  const memoList = React.useMemo(() => {
    return (
      <PaginatedOrderList
        ordersForListing={ordersForListing}
        currentPage={currentPage}
        currentPageSize={currentPageSize}
        amountOfPages={amountOfPages}
        totalAmount={totalAmount}
        selectAll={() => selectAll()}
        selectOrder={(orderId) => selectOrder(orderId)}
        separationType={separationType}
        filterAreaHeight={0}
        pageSize={pageSize}
        changePageSize={(size) => changePageSize(size)}
        changePage={(page) => changePage(page)}
        filtering={filtering}
        refreshOrder={handleRefreshOrder}
        expressOrder={expressOrder}
        sortingOptions={session?.userProfileConfig?.sortingOptions}
        changeOrderBy={changeOrderBy}
        currentOrderBy={currentOrderBy}
      />
    );
  }, [ordersForListing, currentPage, amountOfPages, totalAmount, pageSize, filtering, expressOrder, session?.userProfileConfig?.sortingOptions, currentOrderBy]);

  const memoModalUpdateStateForSelectedOrdersByGlobalPickerRender = React.useMemo(() => {
    if (!modalUpdateStateOrderByGlobalPickerVisible || !globalPickerPerformTypeForModal) return null;

    return (
      <ModalUpdateStateForSelectedOrdersByGlobalPicker
        userId={session?.user?.id}
        selectedOrders={selectedOrdersForModalUpdateStateOrderByGlobalPicker}
        globalPickerPerformType={globalPickerPerformTypeForModal}
        close={() => closeModalUpdateStateOrdersByGlobalPicker()}
        finish={() => finishModalUpdateStateOrdersByGlobalPicker()}
      />
    );
  }, [selectedOrdersForModalUpdateStateOrderByGlobalPicker, modalUpdateStateOrderByGlobalPickerVisible, globalPickerPerformTypeForModal, session]);

  return (
    <Container>
      {memoModalFilter}
      {memoAppliedFiltersToListOrders}
      <ActionsPanel>
        {memoActionsForOrderSelection}
      </ActionsPanel>
      {memoList}
      {memoModalUpdateStateForSelectedOrdersRender}
      {memoModalUpdatePrescriptionStateForSelectedOrdersRender}
      {memoModalNotifyCustomersToCollectRecipeAndDNPsRender}
      {memoModalUpdateStateForSelectedOrdersByGlobalPickerRender}
      {danfeLabelPreGenerationModalData.visible ? (
        <DanfeLabelPreGenerationModal
          close={() => setDanfeLabelPreGenerationModalData({
            visible: false,
            ordersForListing: [],
            fileFormat: null,
          })}
          ordersForListing={danfeLabelPreGenerationModalData.ordersForListing}
          fileFormat={danfeLabelPreGenerationModalData.fileFormat!}
        />
      ) : null}
      {longRunningReportProgressModalVisible ? (
        <LongRunningReportProgressModal
          close={() => setLongRunningReportProgressModalVisible(false)}
          reportType={longRunningReportProgressModalData.reportType}
          reportVersion={longRunningReportProgressModalData.reportVersion}
          orderForListing={longRunningReportProgressModalData.orderForListing}
          queueJob={longRunningReportProgressModalData.queueJob}
          separationType={longRunningReportProgressModalData.separationType}
          fileFormat={longRunningReportProgressModalData.fileFormat}
          barcodeMode={longRunningReportProgressModalData.barcodeMode}
          danfeLabelBarcodeMode={longRunningReportProgressModalData.danfeLabelBarcodeMode}
        />
      ) : null}
    </Container>
  );

  function changeOrderBy(orderBy?: string|null) {
    setCurrentOrderBy(orderBy);
    setCurrentPage(1);
    updateList(pageSize, 1, orderBy);
  }

  function changePage(page: number) {
    setCurrentPage(page);
    updateList(pageSize, page, currentOrderBy);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    setCurrentPage(1);
    updateList(size, 1, currentOrderBy);
  }

  function clearFilter(size: number, page: number) {
    if (size === 0) size = session?.user?.profileType === ProfileType.Vendor ? 75 : 25;
    setPageSize(size);
    setCurrentPage(page);
    let defaultFilterValues: IFilterToListOrdersOutput | undefined = undefined;
    let orderBy = null;
    const sortingOptionsLength = session?.userProfileConfig?.sortingOptions?.length || 0;
    if (sortingOptionsLength > 0) {
      orderBy = session?.userProfileConfig?.sortingOptions?.[0] || null;
    }
    setCurrentOrderBy(orderBy);
    setLastFilterValues(defaultFilterValues);
    filter(defaultFilterValues || {}, size, page, orderBy);
  }

  function updateList(size: number, page: number, orderBy?: string | null | undefined) {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }
    setCurrentPage(page);
    filter(lastFilter || {}, size, page, orderBy);
  }

  function changeFilter(values: IFilterToListOrdersOutput) {
    setModalFilterVisible(false);
    setCurrentPage(1);
    setLastFilterValues(values);
    let pageSizeValue = pageSize;
    if (
      values.number ||
      values.numbers ||
      values.orderAlertType === OrderAlertType.OrderIsLate ||
      values.orderAlertType === OrderAlertType.NoCustumer
    ) {
      setPageSize(0);
      pageSizeValue = 0;
    }
    filter(values, pageSizeValue, 1, currentOrderBy);
  }

  function filter(values: IFilterToListOrdersOutput, size: number, page: number, orderBy?: string | null | undefined) {
    mainActions.setNotWait(true);
    const filterInput = new FilterOrderInput();
    filterInput.frontVersion = FrontVersionResources.currentNumber;
    filterInput.separationType = separationType;
    filterInput.expressOrder = expressOrder;
    filterInput.customerName = values.customerName;
    filterInput.customerEmail = values.customerEmail;
    filterInput.number = values.number;
    filterInput.numbers = values.numbers ? values.numbers.replaceAll('#', '').trim() : undefined;
    const statesId = statesService.getWithEquivalents(values.stateIds ? +values.stateIds.split(' ')[0] : undefined, session?.user?.profileType);
    filterInput.stateIds = statesId && statesId.length > 0 ? statesId.join(' ') : undefined;
    filterInput.statePrescriptionId = values.statePrescriptionId && values.statePrescriptionId > 0 ? values.statePrescriptionId : undefined;
    filterInput.statePrescriptionIds = values.statePrescriptionIds ? values.statePrescriptionIds : undefined;
    filterInput.linkedToOrderBatchOption = LinkedToOrderBatchOption.NotLinkedOnly;
    filterInput.pageSize = size > 0 ? size : 1;
    filterInput.currentPage = page;
    filterInput.shouldPaginate = size > 0;
    filterInput.authorizationStartDate = values.startDate ? moment(values.startDate).format('yyyy-MM-DD') : undefined;
    filterInput.authorizationEndDate = values.endDate ? moment(values.endDate).format('yyyy-MM-DD') : undefined;
    filterInput.deliveryDeadlineStartDate = values.deliveryDeadlineStartDate ? moment(values.deliveryDeadlineStartDate).format('yyyy-MM-DD') : undefined;
    filterInput.deliveryDeadlineEndDate = values.deliveryDeadlineEndDate ? moment(values.deliveryDeadlineEndDate).format('yyyy-MM-DD') : undefined;
    filterInput.deliveryStartDate = values.deliveryStartDate ? moment(values.deliveryStartDate).format('yyyy-MM-DD') : undefined;
    filterInput.deliveryEndDate = values.deliveryEndDate ? moment(values.deliveryEndDate).format('yyyy-MM-DD') : undefined;
    filterInput.specialTag = values.specialTag;
    filterInput.orderAlertType = values.orderAlertType;
    filterInput.userId = session?.user?.id;
    filterInput.locationId = userLocations.length == 1 ? userLocations[0] : values.locationId;
    filterInput.sellerId = values.sellerId;
    filterInput.sellerName = values.sellerName;
    filterInput.orderShippingLineState = values.orderAlertType === OrderAlertType.NoShippingLine
      ? OrderShippingLineState.MissingOnly
      : OrderShippingLineState.All;
    filterInput.stateIdsForUserProfile = values.stateIdsForUserProfile;
    if (values.stateIdsForUserProfile) {
      const stateIdForUserProfile = +values.stateIdsForUserProfile.split(' ')[0];
      const stateForUserProfile = statesService.getByUserProfileAndSeparationType(session, separationType)?.find(s => s.id === stateIdForUserProfile);
      filterInput.stateIds = stateForUserProfile
        ? stateForUserProfile.orderStateList.join(' ')
        : undefined;
      const prescriptionStateList = stateForUserProfile?.prescriptionStateList || [];
      if (SessionHelper.canFilterManyPrescriptionStates(session)) {
        filterInput.statePrescriptionId = undefined;
        filterInput.statePrescriptionIds = prescriptionStateList.length > 0 ? prescriptionStateList.join(' ') : undefined;
      } else {
        filterInput.statePrescriptionId = prescriptionStateList.length > 0 ? prescriptionStateList[0] : undefined;
        filterInput.statePrescriptionIds = undefined;
      }
    }
    filterInput.lensMaterialName = values.lensMaterialName?.toLowerCase() == 'todos' ? null : values.lensMaterialName;
    filterInput.lensClassName = values.lensClassName?.toLowerCase() == 'todos' ? null : values.lensClassName;
    filterInput.fulfilledBy = values.fulfilledBy;
    filterInput.originedBy = values.originedBy;
    filterInput.includesCrossChannelSale = null;
    filterInput.onlyCrossChannelSale = false;
    filterInput.fulfillmentLocationIds = null;
    if (session?.user?.profileType === ProfileType.Vendor) {
      if (values.fulfilledBy === omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.filterId) {
        filterInput.includesCrossChannelSale = true;
        filterInput.fulfillmentLocationIds = [1];
      } else if (values.fulfilledBy === omnichannelResources.FulfillmentGroupOptions.Ecommerce.filterId) {
        filterInput.includesCrossChannelSale = true;
        filterInput.onlyCrossChannelSale = true;
        filterInput.fulfillmentLocationIds = [1];
      }
    } else if (session?.user?.profileType === ProfileType.Ecommerce) {
      filterInput.includesCrossChannelSale = true;
      filterInput.onlyCrossChannelSale = null;
      if (values.originedBy == omnichannelResources.OrderOriginGroupOptions.Ecommerce.filterId) {
        filterInput.includesCrossChannelSale = false;
      } else if (values.originedBy == omnichannelResources.OrderOriginGroupOptions.PhysicalStores.filterId) {
        filterInput.onlyCrossChannelSale = true;
      }
    } else if (session?.user?.profileType === ProfileType.Admin) {
      filterInput.includesCrossChannelSale = true;
      filterInput.onlyCrossChannelSale = null;
    }
    filterInput.ignoreDefaultUserProfileFilters = true;
    filterInput.considerDefaultUserProfileFilters = session?.userProfileConfig?.considerDefaultFilters ? true : false;
    if (filterInput.considerDefaultUserProfileFilters ) {
      filterInput.ignoreDefaultUserProfileFilters = values.ignoreDefaultUserProfileFilters;
    }
    filterInput.salesTypeFilter = values.salesTypeFilter || null;
    filterInput.framesQuantity = values.framesQuantity || FramesQuantity.All;
    filterInput.orderBy = orderBy;
    setLastFilter(filterInput);
    handleFilterOrder(filterInput);
    if (listingOrders) listingOrders();
  }

  function finishModalUpdateStateOrders() {
    setModalUpdateStateOrderVisible(false);
    filter(lastFilter || {}, pageSize, currentPage);
  }

  function finishModalUpdateStatePrescriptions() {
    setModalUpdateStatePrescriptionVisible(false);
    filter(lastFilter || {}, pageSize, currentPage);
  }

  function finishModalNotifyCustomersToCollectRecipeAndDNPs() {
    setModalNotifyCustomersToCollectRecipeAndDNPsVisible(false);
    filter(lastFilter || {}, pageSize, currentPage);
  }

  function updateStateOrderInBatch() {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }
    updateStateOrder();
  }

  function updateStatePrescriptionInBatch() {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }
    updateStatePrescription();
  }

  function updateStateOrderByGlobalPickerInBatch(globalPickerPerformType: GlobalPickerPerformType) {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }
    updateStateOrderByGlobalPicker(globalPickerPerformType);
  }

  function finishModalUpdateStateOrdersByGlobalPicker() {
    setModalUpdateStateOrderByGlobalPickerVisible(false);
    filter(lastFilter || {}, pageSize, currentPage);
  }

  function printDanfePackingLabelByFormat(selectedOrders: OrderForListing[], fileFormat: FileFormat, barcodeMode?: DanfeLabelBarcodeMode) {
    if (fileFormat === FileFormat.ZipZpl) {
      setLongRunningReportProgressModalData({
        reportType: ReportType.DanfePackingLabelZipZpl,
        reportVersion: 0,
        orderForListing: selectedOrders,
        queueJob: false,
        separationType: separationType,
        fileFormat: null,
        danfeLabelBarcodeMode: barcodeMode,
      });
      setLongRunningReportProgressModalVisible(true);
      return;
    }

    setLongRunningReportProgressModalData({
      reportType: ReportType.DanfePackingLabelZpl,
      reportVersion: 0,
      orderForListing: selectedOrders,
      queueJob: false,
      separationType: separationType,
      fileFormat: null,
      danfeLabelBarcodeMode: barcodeMode,
    });
    setLongRunningReportProgressModalVisible(true);
  }
}

export { ListOrders };
